/**
 * Emits <link rel="alternate" hreflang> for every locale on the current route.
 *
 * Applied once, site-wide, in app/layouts/default.vue. Uses switchLocalePath —
 * the same resolver the navbar's language switcher uses — so localised dynamic
 * routes (/category/**, /blog/**) resolve to their real counterpart rather than
 * a naive /en prefix.
 *
 * x-default points at BG: it is the default locale and serves the unprefixed
 * URLs (strategy: prefix_except_default).
 */
const HREFLANG: Record<string, string> = {
  bg: 'bg-BG',
  en: 'en-US',
}

export function useHreflang() {
  const switchLocalePath = useSwitchLocalePath()
  const config = useRuntimeConfig()
  const siteUrl = (config.public.siteUrl as string).replace(/\/$/, '')

  // Mirrors the canonical's shape (see useCanonicalUrl): absolute, no query,
  // no trailing slash. An alternate that disagrees with the target page's own
  // canonical is ignored by search engines.
  const absolute = (path: string) => {
    if (!path) return ''
    const clean = path.split('?')[0]!
    const noSlash = clean.length > 1 && clean.endsWith('/') ? clean.slice(0, -1) : clean
    return `${siteUrl}${noSlash}`
  }

  const alternateLinks = computed(() => {
    const links: Array<{ rel: string; hreflang: string; href: string }> = []

    for (const loc of ['bg', 'en'] as const) {
      const href = absolute(switchLocalePath(loc))
      if (href) links.push({ rel: 'alternate', hreflang: HREFLANG[loc]!, href })
    }

    const bgHref = absolute(switchLocalePath('bg'))
    if (bgHref) links.push({ rel: 'alternate', hreflang: 'x-default', href: bgHref })

    return links
  })

  return { alternateLinks }
}
